import React, { forwardRef } from "react";
import formatDateDDMMYYYY from "../../../../Main/formatDate";
import UserCurrency from "../../../../Componets/UserCurrency";

const capitalizeWords = (str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, char => char.toUpperCase());
};

const PaymentReceipt = forwardRef(({
    invoice,
    payment,
    paymentType,
    isMultiple,
    totalPaid,
    remainingBalance
}, ref) => {
    const filterZeroAmountPayments = (payments) => {
        if (!payments) return [];
        if (Array.isArray(payments)) {
            return payments.filter(p => p.amount > 0);
        }
        return payments.amount > 0 ? [payments] : [];
    };

    const injectScopedStyles = () => {
        if (typeof document === 'undefined') return;

        const style = document.createElement("style");
        style.innerHTML = `
      @page {
        size: A4;
        margin: 6% 6% 16% 6%;
        @bottom-left {
          content: "Printed on: ${formatDateDDMMYYYY(new Date().toISOString())}";
          font-size: 10px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          left: 6%;
        }
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font-size: 10px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          right: 6%;
        }
      }

      @media print {
        .payment-receipt {
          page-break-inside: avoid;
        }
        
        .payment-receipt-footer {
          font-size: 10px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          left: 6%;
        }

        .payment-receipt-footer-right {
          font-size: 10px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          right: 6%;
        }
      }
    `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    };

    React.useEffect(() => {
        if (invoice?.branch?.name) {
            const cleanup = injectScopedStyles();
            return cleanup;
        }
    }, [invoice?.branch?.name]);

    if (!invoice || !payment) {
        return (
            <div ref={ref} className="p-8">
                <div>No payment data available</div>
            </div>
        );
    }

    const filteredPayments = filterZeroAmountPayments(payment);

    return (
        <div className="px-24">
            <div ref={ref}>
                {isMultiple && Array.isArray(filteredPayments) ? (
                    filteredPayments.map((singlePayment, index) => (
                        <div key={singlePayment._id} className="payment-receipt mb-8">
                            {index > 0 && <div className="page-break" style={{ pageBreakBefore: 'always' }} />}

                            <PaymentReceiptContent
                                invoice={invoice}
                                payment={singlePayment}
                                isMultiple={true}
                                receiptNumber={index + 1}
                                totalReceipts={filteredPayments.length}
                                totalPaid={totalPaid}
                                remainingBalance={remainingBalance}
                            />
                        </div>
                    ))
                ) : (
                    filteredPayments.length > 0 && (
                        <div className="payment-receipt">
                            <PaymentReceiptContent
                                invoice={invoice}
                                payment={filteredPayments[0]}
                                isMultiple={false}
                                totalPaid={totalPaid}
                                remainingBalance={remainingBalance}
                            />
                        </div>
                    )
                )}
            </div>
        </div>
    );
});

const PaymentReceiptContent = ({
    invoice,
    payment,
    isMultiple,
    receiptNumber,
    totalReceipts,
    totalPaid,
    remainingBalance
}) => {

    const calculateActualTotals = () => {
        if (!payment) return { amount: 0 };

        if (isMultiple && Array.isArray(payment)) {
            const total = payment.reduce((sum, p) => sum + (p.amount || 0), 0);
            return {
                amount: total,
                count: payment.length
            };
        }

        return {
            amount: payment.amount || 0
        };
    };

    const paymentTotals = calculateActualTotals();

    return (
        <div className="mb-8">
            <div className="flex justify-between items-start pb-4">
                <div className="text-black-900 text-[0.65rem]">
                    <div className="text-bold text-md">{capitalizeWords(invoice?.branch?.name)}</div>
                    <div className="text-medium text-xs">
                        Email&nbsp;:&nbsp;
                        {invoice?.branch?.email ? invoice?.branch?.email + ", " : ""}
                        &nbsp;
                    </div>
                    <div className="text-medium text-xs">
                        Phone&nbsp;:&nbsp;
                        {invoice?.branch?.phone ? invoice?.branch?.phone : ""}
                        &nbsp;
                    </div>
                    <div className="text-xs">
                        {invoice?.branch?.area ? invoice?.branch?.area + ", " : ""}
                        {invoice?.branch?.city ? invoice?.branch?.city + ", " : ""}
                        {invoice?.branch?.country ? invoice?.branch?.country : ""}
                    </div>
                    <div className="text-xs">
                        VATIN&nbsp;:&nbsp;
                        {invoice?.branch?.tax ? invoice?.branch?.tax : "N/A"}
                    </div>
                </div>

                <div className="flex flex-col items-center">
                    <div className="text-xl font-bold text-center">
                        PAYMENT RECEIPT
                    </div>
                    {isMultiple && (
                        <div className="text-xs text-gray-600 mt-1">
                            Receipt {receiptNumber} of {totalReceipts}
                        </div>
                    )}
                </div>
            </div>

            <hr className="my-2 border-t-black-900 border-black" />

            <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <div className="grid grid-cols-3 gap-4 text-[0.65rem]">
                    <div>
                        <div className="font-semibold">Invoice Total</div>
                        <div className="text-lg font-bold">{invoice?.total?.toFixed(3)} <UserCurrency /></div>
                    </div>
                    <div>
                        <div className="font-semibold">Total Paid</div>
                        <div className="text-lg font-bold text-green-700">{totalPaid?.toFixed(3)} <UserCurrency /></div>
                    </div>
                    <div>
                        <div className="font-semibold">Balance</div>
                        <div className={`text-lg font-bold ${remainingBalance === 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {remainingBalance?.toFixed(3)} <UserCurrency />
                        </div>
                        <div className="text-xs">
                            {remainingBalance === 0 ? '✓ Fully Paid' : 'Pending'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex justify-between">
                    <div className="w-1/2">
                        <div className="font-semibold text-[0.70rem] mb-2">Customer Information</div>
                        <div className="text-[0.65rem] space-y-1">
                            <div className="font-medium">
                                {invoice?.customer?.name || "Walk-in-customer"}
                            </div>

                            {invoice?.customer?.phone && (
                                <div>
                                    <span className="font-semibold">Phone:</span> {invoice?.customer?.phone}
                                </div>
                            )}

                            {invoice?.customer?.email && (
                                <div>
                                    <span className="font-semibold">Email:</span> {invoice?.customer?.email}
                                </div>
                            )}

                            {invoice?.customer?.city && (
                                <div>
                                    {invoice?.customer?.city ? invoice?.customer?.city + ", " : ""}
                                    {invoice?.customer?.state}
                                </div>
                            )}

                            {invoice?.customer?.country && (
                                <div>{invoice?.customer?.country}</div>
                            )}

                            {invoice?.customer?.tax && (
                                <div>
                                    <span className="font-semibold">VATIN:</span> {invoice?.customer?.tax}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="w-1/2">
                        <div className="font-semibold text-[0.70rem] mb-2">Invoice Information</div>
                        <div className="text-[0.65rem] space-y-1">
                            <div className="flex">
                                <div className="w-32 font-semibold">Invoice Number:</div>
                                <div>#INV-{invoice?.number}</div>
                            </div>

                            <div className="flex">
                                <div className="w-32 font-semibold">Invoice Date:</div>
                                <div>{formatDateDDMMYYYY(invoice?.date)}</div>
                            </div>

                            <div className="flex">
                                <div className="w-32 font-semibold">Invoice Total:</div>
                                <div>{invoice?.total?.toFixed(3)} <UserCurrency /></div>
                            </div>

                            {invoice?.due_date && (
                                <div className="flex">
                                    <div className="w-32 font-semibold">Due Date:</div>
                                    <div>{formatDateDDMMYYYY(invoice?.due_date)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
                <div className="font-semibold text-[0.70rem] mb-3">PAYMENT DETAILS</div>

                {isMultiple && Array.isArray(payment) ? (
                    <div className="mb-4">
                        <div className="text-[0.65rem] mb-3">
                            <div className="flex justify-between items-center">
                                <div>
                                    <div className="font-semibold">Total Selected Payments: {payment.length}</div>
                                    <div>Total Amount: {paymentTotals.amount.toFixed(3)} <UserCurrency /></div>
                                </div>
                            </div>
                        </div>

                        <table className="w-full text-[0.65rem] mb-4">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="text-left p-2">#</th>
                                    <th className="text-left p-2">Payment Mode</th>
                                    <th className="text-left p-2">Date</th>
                                    <th className="text-right p-2">Amount (<UserCurrency />)</th>
                                    <th className="text-center p-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payment.map((p, index) => (
                                    <tr key={p._id} className="border-t">
                                        <td className="p-2">{index + 1}</td>
                                        <td className="p-2">{p.name}</td>
                                        <td className="p-2">{formatDateDDMMYYYY(p.created || p.createdAt)}</td>
                                        <td className="p-2 text-right">{p.amount?.toFixed(3)}</td>
                                        <td className="p-2 text-center">
                                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                                PAID
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t font-bold bg-gray-50">
                                    <td className="p-2" colSpan="3">TOTAL RECEIVED</td>
                                    <td className="p-2 text-right text-green-700">
                                        {paymentTotals.amount.toFixed(3)} <UserCurrency />
                                    </td>
                                    <td className="p-2"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        <div className="text-[0.65rem]">
                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Receipt Number:</div>
                                <div>#PYMT-{payment?._id?.toUpperCase()}</div>
                            </div>

                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Payment Date:</div>
                                <div>{formatDateDDMMYYYY(payment?.created || payment?.createdAt)}</div>
                            </div>

                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Payment Mode:</div>
                                <div className="font-medium">{payment?.name}</div>
                            </div>

                            {payment?.description && (
                                <div className="flex mb-2">
                                    <div className="w-32 font-semibold">Description:</div>
                                    <div>{payment?.description}</div>
                                </div>
                            )}
                        </div>

                        <div className="text-[0.65rem]">
                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Payment Status:</div>
                                <div className="font-medium text-green-600">COMPLETED</div>
                            </div>

                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Processed By:</div>
                                <div>{invoice?.created_by?.name}</div>
                            </div>

                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Branch:</div>
                                <div>{capitalizeWords(invoice?.branch?.name)}</div>
                            </div>

                            <div className="flex mb-2">
                                <div className="w-32 font-semibold">Payment Amount:</div>
                                <div className="text-lg font-bold text-green-700">
                                    {payment?.amount?.toFixed(3)} <UserCurrency />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
                <div className="font-semibold text-[0.70rem] mb-2">Terms & Conditions</div>
                <div className="text-[0.60rem] text-gray-600">
                    <ol className="list-decimal pl-4 space-y-1">
                        <li>This is an official receipt for payment made against invoice #{invoice?.number}</li>
                        <li>Please retain this receipt for your records</li>
                        <li>Any discrepancies must be reported within 7 days</li>
                        <li>Receipt is valid only for the amount and invoice mentioned above</li>
                        <li>For queries, contact {invoice?.branch?.phone || 'the branch'}</li>
                    </ol>
                </div>
            </div>

            <div className="flex justify-between pt-8 text-[0.60rem]">
                <div className="text-center">
                    <div className="border-b border-gray-400 w-32 mb-1"></div>
                    <div className="font-semibold">Customer Signature</div>
                    <div className="text-xs text-gray-500">Date: ___________</div>
                </div>
                <div className="text-center">
                    <div className="border-b border-gray-400 w-32 mb-1"></div>
                    <div className="font-semibold">Cashier Signature</div>
                    <div className="text-xs text-gray-500">Name: {invoice?.created_by?.name}</div>
                </div>
                <div className="text-center">
                    <div className="border-b border-gray-400 w-32 mb-1"></div>
                    <div className="font-semibold">Branch Stamp</div>
                    <div className="text-xs text-gray-500">{capitalizeWords(invoice?.branch?.name)}</div>
                </div>
            </div>

            <div className="mt-8 text-center text-[0.55rem] text-gray-500">
                <div>Printed on: {formatDateDDMMYYYY(new Date().toISOString())}</div>
                <div>This is a computer generated receipt and does not require a physical signature</div>
                <div className="mt-1">
                    <span>
                        {remainingBalance === 0 ? (
                            '✓ This invoice has been fully paid'
                        ) : (
                            <>
                                ℹ️ Remaining balance: {remainingBalance?.toFixed(3)} <UserCurrency />
                            </>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default PaymentReceipt;